'use client';

import React, { useState, useEffect, Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { articlesApi } from '@/app/api/articles';
import { Button } from '@/components/common/Button';
import TaggedSelect from '@/components/common/TaggedSelect';
import Tooltip from '@/components/common/Tooltip';
import { CheckCircleIcon, ExclamationTriangleIcon, XCircleIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';
import { validateGeoMetadata, GeoIssue } from '../utils/geo-validation';

interface ArticlePublishWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onPublish: (slug: string, meta: any) => Promise<void>;
    articleId: string;
    articleTitle: string;
    initialMeta: any;
    articleContent: string; // Needed for validation
    availableTags: { value: string; label: string }[];
    availableCategories: { value: string; label: string }[];
    isPublishing: boolean;
}

// Generate slug helper (copied from SlugPublishModal)
function generateSlug(title: string): string {
    if (!title) return '';
    const stopWords = new Set(['a', 'an', 'the', 'of', 'in', 'on', 'at', 'to', 'for', 'is', 'are', 'and', 'or', 'but', 'with', 'by', 'from', 'up', 'about', 'into', 'over', 'after']);
    const normalized = title.toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '');
    const words = normalized.split(/[\s-]+/).filter(w => w.length > 0);
    const meaningfulWords = words.filter(w => !stopWords.has(w));
    const finalWords = meaningfulWords.length >= 2 ? meaningfulWords : words;
    return finalWords.slice(0, 5).join('-');
}

export default function ArticlePublishWizard({
    isOpen,
    onClose,
    onPublish,
    articleId,
    articleTitle,
    initialMeta,
    articleContent,
    availableTags,
    availableCategories,
    isPublishing
}: ArticlePublishWizardProps) {
    const [step, setStep] = useState(1);
    const [meta, setMeta] = useState<any>(initialMeta || {});
    const [slug, setSlug] = useState('');

    // Validation State
    const [step1Error, setStep1Error] = useState<string | null>(null);
    const [warnings, setWarnings] = useState<GeoIssue[]>([]);

    // Slug Validation State
    const [isValidatingSlug, setIsValidatingSlug] = useState(false);
    const [slugError, setSlugError] = useState<string | null>(null);
    const [isSlugValid, setIsSlugValid] = useState(false);

    // Initialize state on open
    useEffect(() => {
        if (isOpen) {
            setStep(1);
            setMeta(initialMeta || {});
            // Pre-generate slug
            const generated = initialMeta?.slug || generateSlug(articleTitle);
            setSlug(generated);
            if (generated) validateSlug(generated);
        }
    }, [isOpen, initialMeta, articleTitle]);

    const validateSlug = async (slugToValidate: string) => {
        if (!slugToValidate || !slugToValidate.trim()) {
            setSlugError('Slug cannot be empty');
            setIsSlugValid(false);
            return;
        }
        if (!/^[a-z0-9-]+$/.test(slugToValidate)) {
            setSlugError('Slug can only contain lowercase letters, numbers, and hyphens');
            setIsSlugValid(false);
            return;
        }
        if (slugToValidate.startsWith('-') || slugToValidate.endsWith('-')) {
            setSlugError('Slug cannot start or end with a hyphen');
            setIsSlugValid(false);
            return;
        }
        if (slugToValidate.includes('--')) {
            setSlugError('Slug cannot contain consecutive hyphens');
            setIsSlugValid(false);
            return;
        }

        setIsValidatingSlug(true);
        setSlugError(null);
        try {
            const result = await articlesApi.validateSlug(articleId, slugToValidate);
            if (result.valid) {
                setIsSlugValid(true);
                setSlugError(null);
            } else {
                setIsSlugValid(false);
                setSlugError(result.error || 'This slug is already in use');
            }
        } catch (error) {
            setIsSlugValid(false);
            setSlugError('Failed to validate slug');
        } finally {
            setIsValidatingSlug(false);
        }
    };

    const handleSlugChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newSlug = e.target.value;
        setSlug(newSlug);
        // Debounce logic could be added here, but simple call for now
        // Actually, debounce is good.
        // Simplifying for now: immediate validation on blur or simple delay if I added it.
        // I'll call validate immediately but maybe throttle in real implementation.
        // Let's stick to the timeout approach from SlugPublishModal for best UX.
    };

    // Effect to debounce slug validation
    useEffect(() => {
        if (!slug) return;
        const timer = setTimeout(() => validateSlug(slug), 500);
        return () => clearTimeout(timer);
    }, [slug]);


    const handleNext = () => {
        if (step === 1) {
            // Step 1 Validation: Required Fields
            if (!meta.category) {
                setStep1Error('Please select a category.');
                return;
            }
            if (!meta.tags || meta.tags.length === 0) {
                setStep1Error('Please add at least one tag.');
                return;
            }
            setStep1Error(null);

            // Run GEO Validation
            const newWarnings = validateGeoMetadata(
                meta.citations || [],
                meta.mentions || [],
                articleContent
            );
            setWarnings(newWarnings);
            setStep(2);
        } else if (step === 2) {
            setStep(3);
        } else {
            // Publish
            if (isSlugValid && !isValidatingSlug) {
                onPublish(slug, meta);
            }
        }
    };

    const handleBack = () => {
        if (step > 1) setStep(step - 1);
    };

    // Render Steps
    const renderStep1 = () => (
        <div className="space-y-6">
            <div className="bg-blue-50 p-4 rounded-md mb-4">
                <h4 className="text-sm font-medium text-blue-800">Step 1: Optimize for Search (GEO)</h4>
                <p className="text-xs text-blue-600 mt-1">Add citations and entities to help AI Search Engines understand and rank your content.</p>
            </div>

            {step1Error && (
                <div className="bg-red-50 p-3 rounded-md border border-red-200">
                    <p className="text-sm text-red-600">{step1Error}</p>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                    <TaggedSelect
                        value={meta.category}
                        onChange={(val) => setMeta({ ...meta, category: Array.isArray(val) ? val[0] : val })}
                        options={availableCategories}
                        placeholder="Select category..."
                        multiple={false}
                        editable={false}
                    />
                </div>
                <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags <span className="text-red-500">*</span></label>
                    <TaggedSelect
                        value={meta.tags}
                        onChange={(val) => setMeta({ ...meta, tags: val })}
                        options={availableTags}
                        placeholder="Add tags..."
                        multiple={true}
                        editable={true}
                    />
                </div>
            </div>

            <div>
                <div className="flex items-center gap-1 mb-1">
                    <span className="text-sm font-medium text-gray-700">Citations / Sources</span>
                    <Tooltip inline content="External links to authoritative sources (e.g. Wikipedia, Official Docs). This builds trust and authority (E-E-A-T) for your content.">
                        <QuestionMarkCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" />
                    </Tooltip>
                </div>
                <TaggedSelect
                    value={meta.citations}
                    onChange={(val) => setMeta({ ...meta, citations: val })}
                    options={[]}
                    placeholder="Add URL (e.g. https://wikipedia.org...)"
                    multiple={true}
                    editable={true}
                    onCreateOption={(val) => setMeta({ ...meta, citations: [...(meta.citations || []), val] })}
                />
                <p className="text-xs text-gray-500 mt-1">Link to 3-5 authoritative sources.</p>
            </div>

            <div>
                <div className="flex items-center gap-1 mb-1">
                    <span className="text-sm font-medium text-gray-700">Mentions (Entities)</span>
                    <Tooltip inline content="Key people, organizations, or products discussed in the article. Helping AI identify these entities improves relevancy matching.">
                        <QuestionMarkCircleIcon className="w-4 h-4 text-gray-400 cursor-help hover:text-gray-600" />
                    </Tooltip>
                </div>
                <TaggedSelect
                    value={meta.mentions}
                    onChange={(val) => setMeta({ ...meta, mentions: val })}
                    options={[]}
                    placeholder="Add entity name (e.g. Google, React)"
                    multiple={true}
                    editable={true}
                    onCreateOption={(val) => setMeta({ ...meta, mentions: [...(meta.mentions || []), val] })}
                />
                <p className="text-xs text-gray-500 mt-1">Tag key people, organizations, or products discussed.</p>
            </div>
        </div>
    );

    const renderStep2 = () => (
        <div className="space-y-4">
            {warnings.length === 0 ? (
                <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
                    <CheckCircleIcon className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <h3 className="text-lg font-medium text-green-900">Optimization Check Passed!</h3>
                    <p className="text-sm text-green-700 mt-1">Your article metadata matches your content well.</p>
                    <div className="mt-4 bg-white/50 p-2 rounded text-xs text-green-800 border border-green-100">
                        <strong>Note on Authenticity:</strong> Only cite sources and tag entities that are genuinely relevant to your content. Over-optimization or "stuffing" can hurt your ranking.
                    </div>
                </div>
            ) : (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                    <div className="flex items-center mb-4">
                        <ExclamationTriangleIcon className="w-8 h-8 text-yellow-600 mr-3" />
                        <h3 className="text-lg font-medium text-yellow-900">Optimization Suggestions</h3>
                    </div>
                    <ul className="space-y-3">
                        {warnings.map((w, i) => (
                            <li key={i} className="flex flex-col bg-white p-3 rounded border border-yellow-100 shadow-sm">
                                <div className="flex items-start">
                                    <span className="mr-2 text-yellow-500">⚠️</span>
                                    <span className="text-sm text-gray-700">{w.message.replace(/^⚠️\s*/, '')}</span>
                                </div>
                                {w.items && w.items.length > 0 && (
                                    <ul className="mt-2 ml-7 list-disc text-xs text-gray-600 space-y-1">
                                        {w.items.map((item, j) => (
                                            <li key={j}>{item}</li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        ))}
                    </ul>
                    <p className="text-sm text-gray-500 mt-4 text-center">You can go back to fix these, or proceed anyway.</p>
                </div>
            )}
        </div>
    );

    const renderStep3 = () => (
        <div className="space-y-6">
            <div className="bg-gray-50 p-4 rounded-md">
                <h4 className="text-sm font-medium text-gray-900">Final Step: Choose your URL</h4>
                <p className="text-xs text-gray-500 mt-1">Customize how your article link will look.</p>
            </div>

            <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Article Slug</label>
                <div className="relative">
                    <input
                        type="text"
                        value={slug}
                        onChange={handleSlugChange}
                        disabled={isPublishing}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500"
                        placeholder="my-article-slug"
                    />
                    {isValidatingSlug && (
                        <div className="absolute right-3 top-2.5">
                            <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                        </div>
                    )}
                </div>
                {slugError ? (
                    <p className="mt-1 text-sm text-red-600">{slugError}</p>
                ) : isSlugValid ? (
                    <p className="mt-1 text-sm text-green-600">✓ Slug is available</p>
                ) : null}
                <p className="text-xs text-gray-500 mt-2">
                    Preview: <span className="font-mono text-blue-600">/articles/{slug}</span>
                </p>
            </div>
        </div>
    );

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/30" />
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Dialog.Panel className="w-full max-w-2xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                            <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-gray-900 mb-6 flex justify-between items-center">
                                <span>Publish Article</span>

                                {/* Stepper Indicator */}
                                <div className="flex items-center space-x-2 text-sm">
                                    <span className={`px-2 py-1 rounded ${step === 1 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>1. Optimize</span>
                                    <span className="text-gray-300">→</span>
                                    <span className={`px-2 py-1 rounded ${step === 2 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>2. Review</span>
                                    <span className="text-gray-300">→</span>
                                    <span className={`px-2 py-1 rounded ${step === 3 ? 'bg-blue-600 text-white' : 'bg-gray-100'}`}>3. Publish</span>
                                </div>
                            </Dialog.Title>

                            {step === 1 && renderStep1()}
                            {step === 2 && renderStep2()}
                            {step === 3 && renderStep3()}

                            <div className="mt-8 flex justify-between">
                                <Button plain onClick={step === 1 ? onClose : handleBack} disabled={isPublishing}>
                                    {step === 1 ? 'Cancel' : 'Back'}
                                </Button>
                                <Button onClick={handleNext} disabled={isPublishing || (step === 3 && (!isSlugValid || isValidatingSlug))}>
                                    {isPublishing ? 'Publishing...' : step === 3 ? 'Publish Now' : 'Next'}
                                </Button>
                            </div>

                        </Dialog.Panel>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
}
